// The scorecard: one row per metric, grouped primary / guardrails / secondary,
// each treatment against control with change, interval bar, p-value and
// a verdict. Same reading as Statsig Pulse or GrowthBook's results table.
import React from 'react';
import { Alert, Segmented, Space, Table, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  InfoCircleOutlined,
  MinusCircleOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import type {
  ExpV2Comparison,
  ExpV2MetricResult,
  ExpV2MetricRole,
  ExpV2Population,
  ExpV2Results,
  ExpV2Verdict,
} from '@/types/experimentsV2';
import { istTime } from './display';
import {
  VERDICT_STYLE,
  describeChange,
  formatCount,
  formatP,
  formatValue,
  intervalLevel,
  intervalOf,
  isCatalogueMetric,
} from './results';

const WINDOW_LABEL: Record<ExpV2MetricResult['window'], string> = {
  '24h': 'first 24 h',
  '7d': 'first 7 days',
  d1: 'IST day 1',
  d3: 'IST day 3',
  d7: 'IST day 7',
};

const VERDICT_ICON: Record<ExpV2Verdict, React.ReactNode> = {
  better: <CheckCircleFilled />,
  worse: <CloseCircleFilled />,
  neutral: <MinusCircleOutlined />,
  not_enough_data: <QuestionCircleOutlined />,
};

export const VerdictTag: React.FC<{ verdict: ExpV2Verdict }> = ({ verdict }) => {
  const s = VERDICT_STYLE[verdict];
  return (
    <Tag color={s.tag} icon={VERDICT_ICON[verdict]} data-verdict={verdict}>
      {s.label}
    </Tag>
  );
};

const BAR_W = 140;
const BAR_H = 18;

/** The interval (95%, wider with Bonferroni) as a bar around zero; the dot is the estimate. */
export const CiBar: React.FC<{
  comparison: ExpV2Comparison;
  scale: number;
  text: string;
  level: string;
}> = ({ comparison, scale, text, level }) => {
  const iv = intervalOf(comparison);
  const color = VERDICT_STYLE[comparison.verdict].color;
  const half = BAR_W / 2 - 6;
  const x = (v: number) => BAR_W / 2 + Math.max(-1, Math.min(1, v / scale)) * half;
  return (
    <Tooltip title={iv ? `${level} interval ${text}` : 'Not enough data'}>
      <svg
        width={BAR_W}
        height={BAR_H}
        role="img"
        aria-label={iv ? `${level} interval ${text}` : 'no interval'}
        style={{ display: 'block' }}
      >
        <line
          x1={BAR_W / 2}
          x2={BAR_W / 2}
          y1={0}
          y2={BAR_H}
          stroke="#595959"
          strokeWidth={1}
          strokeDasharray="2 2"
        />
        {iv && (
          <>
            <line
              x1={x(iv.low)}
              x2={x(iv.high)}
              y1={BAR_H / 2}
              y2={BAR_H / 2}
              stroke={color}
              strokeWidth={2}
              strokeLinecap="round"
            />
            <circle
              cx={x(iv.mid)}
              cy={BAR_H / 2}
              r={4}
              fill={color}
              stroke="#fff"
              strokeWidth={2}
            />
          </>
        )}
      </svg>
    </Tooltip>
  );
};

const ROLE_TITLE: Record<ExpV2MetricRole, string> = {
  primary: 'Primary metric',
  guardrail: 'Guardrails (must not get worse)',
  secondary: 'Secondary metrics',
};

/** Largest interval end of a metric across treatments, so its bars share one scale. */
function scaleOf(m: ExpV2MetricResult): number {
  const ends = m.comparisons
    .map(intervalOf)
    .flatMap((iv) => (iv ? [Math.abs(iv.low), Math.abs(iv.high)] : []))
    .filter(Number.isFinite);
  return Math.max(1e-9, ...ends);
}

function columns(r: ExpV2Results): ColumnsType<ExpV2MetricResult> {
  const arms = r.variants.map((v) => v.variant);
  const treatments = arms.filter((v) => v !== r.control);
  return [
    {
      title: 'Metric',
      key: 'metric',
      fixed: 'left',
      width: 240,
      render: (_: unknown, m) => (
        <Space direction="vertical" size={0}>
          <Space size={4}>
            <Typography.Text strong>{m.label}</Typography.Text>
            <Tooltip title={m.description}>
              <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
            </Tooltip>
          </Space>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {WINDOW_LABEL[m.window]} · {m.better} is better
          </Typography.Text>
        </Space>
      ),
    },
    ...arms.map((v) => ({
      title: v,
      key: `arm-${v}`,
      width: 110,
      align: 'right' as const,
      render: (_: unknown, m: ExpV2MetricResult) => {
        const a = m.arms.find((x) => x.variant === v);
        return (
          <Space direction="vertical" size={0} style={{ alignItems: 'flex-end' }}>
            <Typography.Text>{formatValue(m.unit, a?.mean)}</Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              n {formatCount(a?.n ?? 0)}
            </Typography.Text>
          </Space>
        );
      },
    })),
    ...treatments.map((v) => ({
      title: treatments.length > 1 ? `${v} vs ${r.control}` : `Change vs ${r.control}`,
      key: `cmp-${v}`,
      children: [
        {
          title: 'Change',
          key: `change-${v}`,
          width: 100,
          align: 'right' as const,
          render: (_: unknown, m: ExpV2MetricResult) => {
            const c = m.comparisons.find((x) => x.variant === v);
            if (!c || c.verdict === 'not_enough_data') return '—';
            const d = describeChange(m, c);
            return (
              <Space direction="vertical" size={0} style={{ alignItems: 'flex-end' }}>
                <Typography.Text strong>{d.main}</Typography.Text>
                {d.detail && (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {d.detail}
                  </Typography.Text>
                )}
              </Space>
            );
          },
        },
        {
          title: `${intervalLevel(r.alpha)} interval`,
          key: `ci-${v}`,
          width: 170,
          render: (_: unknown, m: ExpV2MetricResult) => {
            const c = m.comparisons.find((x) => x.variant === v);
            if (!c) return '—';
            const text = c.verdict === 'not_enough_data' ? '' : describeChange(m, c).interval;
            return (
              <Space direction="vertical" size={0}>
                <CiBar
                  comparison={c}
                  scale={scaleOf(m)}
                  text={text}
                  level={intervalLevel(r.alpha)}
                />
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {text}
                </Typography.Text>
              </Space>
            );
          },
        },
        {
          title: 'p-value',
          key: `p-${v}`,
          width: 90,
          align: 'right' as const,
          render: (_: unknown, m: ExpV2MetricResult) =>
            formatP(m.comparisons.find((x) => x.variant === v)?.pValue ?? null),
        },
        {
          title: 'Result',
          key: `verdict-${v}`,
          width: 150,
          render: (_: unknown, m: ExpV2MetricResult) => {
            const c = m.comparisons.find((x) => x.variant === v);
            return <VerdictTag verdict={c?.verdict ?? 'not_enough_data'} />;
          },
        },
      ],
    })),
  ];
}

interface Props {
  results: ExpV2Results | null;
  loading: boolean;
  error: string | null;
  population: ExpV2Population;
  onPopulation: (p: ExpV2Population) => void;
}

export const Scorecard: React.FC<Props> = ({
  results,
  loading,
  error,
  population,
  onPopulation,
}) => {
  const pre = results?.preRegistration;
  const freeText = [
    ...(pre?.primaryMetric && !isCatalogueMetric(pre.primaryMetric)
      ? [`primary metric "${pre.primaryMetric}"`]
      : []),
    ...(pre?.guardrails ?? []).filter((g) => !isCatalogueMetric(g)).map((g) => `guardrail "${g}"`),
  ];
  const treatments = results
    ? results.variants.filter((v) => v.variant !== results.control).length
    : 0;
  const groups = (['primary', 'guardrail', 'secondary'] as const)
    .map((role) => ({ role, rows: results?.metrics.filter((m) => m.role === role) ?? [] }))
    .filter((g) => g.rows.length > 0);

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space wrap>
        <Segmented<ExpV2Population>
          value={population}
          onChange={onPopulation}
          options={[
            { value: 'assigned', label: 'All assigned players (intent to treat)' },
            { value: 'exposed', label: 'Exposed players only' },
          ]}
        />
        {results && (
          <Typography.Text type="secondary">
            {results.variants.map((v) => `${v.variant} ${formatCount(v.players)}`).join(' · ')}{' '}
            players · computed {istTime(results.computedAt)} IST (refreshes every 10 min)
          </Typography.Text>
        )}
      </Space>
      {population === 'exposed' && (
        <Alert
          type="info"
          showIcon
          message="Exposed only counts players who saw the tested surface, from their first exposure. If one arm shows the surface to more players than the other, this read is biased; prefer all assigned players."
        />
      )}
      {freeText.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message={`Not scored here: the ${freeText.join(', ')}. They are free text, not standard metrics; read them with the custom analysis.`}
        />
      )}
      {error && <Alert type="error" showIcon message={error} />}
      {groups.map((g) => (
        <div key={g.role} data-role={g.role}>
          <Typography.Title level={5} style={{ marginTop: 0 }}>
            {ROLE_TITLE[g.role]}
          </Typography.Title>
          <Table<ExpV2MetricResult>
            size="small"
            rowKey="key"
            bordered
            loading={loading}
            pagination={false}
            dataSource={g.rows}
            columns={columns(results!)}
            tableLayout="fixed"
            scroll={{ x: 'max-content' }}
          />
        </div>
      ))}
      {!results && loading && (
        <Typography.Text type="secondary">Computing results…</Typography.Text>
      )}
      {results && (
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
          How to read: each row compares a variant with control on players assigned since the full
          range opened (QA-forced players and players seen in two variants are left out). A player
          counts for a metric only once its window has closed, so n differs by row. The bar is the
          {intervalLevel(results.alpha)} interval of the change (relative to control); the dot is
          the estimate and the dashed line is no change. Better / Worse means p &lt;{' '}
          {Number(results.alpha.toPrecision(2))}
          {treatments > 1 ? ` (0.05 split across ${treatments} variants, Bonferroni)` : ''};
          anything else is No clear change. Checking results many times before the planned end
          raises the chance of a false win: decide at the planned sample.
        </Typography.Paragraph>
      )}
    </Space>
  );
};
